import Vue, { ref } from 'vue';
import {
    ReviewSummaryOverview,
    ReviewSummaryTeaser,
    fetchReviewSummary,
    createReviewSummaryTracking,
} from '@features/reviewSummary';
import HotelTrackingService from '@ve/pages/hotel/analytics/yandexAnalytics/hotelTrackingService';
import { HotelReviews, getHotelReviews, MAX_SIZE_REVIEWS } from '@widgets/hotelReviews';
import OurReviewsList from './ourReviews/OurReviewsList';
import TopHotelsReviews from './tophotels/TopHotelsReviews';

import './reviews.scss';

export default class HotelPageReviewsView extends Backbone.Epoxy.View {
    initialize(options) {
        this.hotelTrackingService = new HotelTrackingService();
        this.tourSearchModel = options?.tourSearchModel ?? null;
        this.initDefaultState();
        this.initTabs();
        this.initTabEvents();
        this.initModules();
        this.initAnalytics();
        this.initDefaultActiveTabs();
    }

    initDefaultState() {
        this.topHotelReviewsLoaded = false;
        this.tripAdvisorData = ref(null);
        this.tripAdvisorRendered = false;
    }

    initModules() {
        this.loadTripAdvisorHotelReviews();
        this.renderReviewSummary();

        this.topHotels = new TopHotelsReviews({
            el: this.$el.find('#reviews_tophotels .frame'),
        });

        this.ourReviewsList = new OurReviewsList({
            el: this.$el,
        });
    }

    async renderReviewSummary() {
        const hotelId = window.appConfig.hotelInfo?.id;
        if (!hotelId) {
            return;
        }

        const summary = await fetchReviewSummary(hotelId);
        if (!summary) {
            return;
        }

        const tracking = createReviewSummaryTracking({ hotelId, summary });
        tracking.pageView();

        const overview = new Vue({
            render: (h) =>
                h(ReviewSummaryOverview, {
                    on: { 'expand-criteria': () => tracking.criteriaExpandClick() },
                }),
        }).$mount('.js-review-summary-overview');

        overview.$nextTick(() => {
            tracking.observeImpression(document.querySelector('#review-summary-overview'));
        });

        const teaserEl = document.querySelector('.js-review-summary-teaser');
        if (teaserEl) {
            new Vue({
                render: (h) =>
                    h(ReviewSummaryTeaser, {
                        on: {
                            'scroll-to-overview': () => {
                                tracking.teaserExpandClick();
                                this.scrollToReviewSummary();
                            },
                        },
                    }),
            }).$mount(teaserEl);
        }
    }

    scrollToReviewSummary() {
        const target = document.querySelector('#review-summary-overview');
        if (!target) {
            return;
        }

        const FALLBACK_HEADER_HEIGHT = 90;
        const fixedForm = document.querySelector('.serp-page__form_fixed');
        const headerHeight = fixedForm?.offsetHeight || FALLBACK_HEADER_HEIGHT;
        target.style.scrollMarginTop = `${headerHeight}px`;

        const SMOOTH_SCROLL_GUARD_MS = 900;
        let expectedScrollY = null;
        let guardUntil = 0;

        const alignToTarget = (smooth = true) => {
            $('html, body').stop(true);
            target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
            if (smooth) {
                guardUntil = Date.now() + SMOOTH_SCROLL_GUARD_MS;
            } else {
                expectedScrollY = Math.round(window.scrollY);
            }
        };
        alignToTarget();

        const isSearchPending =
            this.tourSearchModel?.get('beforeToursLoading') || this.tourSearchModel?.get('loadingInProgress');
        if (!isSearchPending) {
            return;
        }

        const SETTLE_AFTER_COMPLETE_MS = 2000;
        const MAX_LIFETIME_MS = 40000;
        const USER_SCROLL_TOLERANCE_PX = 4;
        const USER_SCROLL_CONFIRM_MS = 60;
        const USER_INTERRUPT_EVENTS = ['wheel', 'touchstart', 'keydown'];

        const keepAligned = new ResizeObserver(() => alignToTarget(false));
        keepAligned.observe(document.body);

        let settleTimer;
        const stop = () => {
            clearTimeout(settleTimer);
            expectedScrollY = null;
            keepAligned.disconnect();
            this.tourSearchModel.off('change:loadingInProgress', onLoadingChange);
            window.removeEventListener('scroll', onScroll);
            USER_INTERRUPT_EVENTS.forEach((eventName) => window.removeEventListener(eventName, stop));
        };
        const onLoadingChange = () => {
            alignToTarget(false);
            if (!this.tourSearchModel.get('loadingInProgress')) {
                clearTimeout(settleTimer);
                settleTimer = setTimeout(stop, SETTLE_AFTER_COMPLETE_MS);
            }
        };
        const onScroll = () => {
            if (Date.now() < guardUntil) {
                expectedScrollY = Math.round(window.scrollY);
                return;
            }
            if (expectedScrollY === null || Math.abs(window.scrollY - expectedScrollY) <= USER_SCROLL_TOLERANCE_PX) {
                return;
            }
            setTimeout(() => {
                if (expectedScrollY !== null && Math.abs(window.scrollY - expectedScrollY) > USER_SCROLL_TOLERANCE_PX) {
                    stop();
                }
            }, USER_SCROLL_CONFIRM_MS);
        };
        this.tourSearchModel.on('change:loadingInProgress', onLoadingChange);
        window.addEventListener('scroll', onScroll, { passive: true });
        USER_INTERRUPT_EVENTS.forEach((eventName) =>
            window.addEventListener(eventName, stop, { passive: true }));
        setTimeout(stop, MAX_LIFETIME_MS);
    }

	initAnalytics() {
		this.ourReviewsList.model.once('change:numOfComments', () => {
			if(this.ourReviewsList.model.get('numOfComments')) {
                this.hotelTrackingService.trackAvailableTravelata();
			}
		});
        this.ourReviewsList.model.on('change:pageNumber', () => {
            this.hotelTrackingService.trackReviewsTravelataMoreClick();
        });

        document.querySelector('.show-all-reviews')?.addEventListener('click', () => {
            this.hotelTrackingService.trackTravelataShowAllClick();
        });

        document.querySelector('.js-open-review-form')?.addEventListener('click', () => {
            this.hotelTrackingService.trackTravelataNewReviewClick();
        });

        this.ourReviewsList.reviewForm.model.on('change:isReviewSent', () => {
            if(this.ourReviewsList.reviewForm.model.get('isReviewSent')) {
                this.hotelTrackingService.trackReviewsTravelataNewReviewSuccess();
            }
        });

        document.querySelector('.allReviewsLink')?.addEventListener('click', () => {
            this.hotelTrackingService.trackShowReviewsClick();
        });
	}
    initTabs() {
        this.$el.tabs();
    }
    

    initDefaultActiveTabs() {
        const reviewsSource = window.appConfig.hotelInfo?.reviewsSource;
        const allReviews = {
            topHotels: { active: 0 },
            travelata: { active: 1 },
            tripadvisor: { active: 2 },
        };

        const reviewsTabsEl = document.querySelector('#reviewsTabs');
        if (reviewsTabsEl && 'IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.hotelTrackingService.trackShowReviewsSource({ reviewsSource });
                        obs.disconnect();
                    }
                });
            });
            observer.observe(reviewsTabsEl);
        }
        $('#reviewsTabs').tabs(allReviews[reviewsSource]);
        this.$el.trigger('tabsactivate');
    }

    async loadTripAdvisorHotelReviews() {
        const options = {
            limit: MAX_SIZE_REVIEWS * 2,
            hotelId: window.appConfig.hotelInfo?.id,
            page: 1,
        };

        const res = await getHotelReviews(options);

        if (res.reviews?.length) {
            this.tripAdvisorData.value = res;
            this.hotelTrackingService.trackAvailableTripadvisor();
        } else {
            this.hideTripAdvisorTab();
        }
    }

    hideTripAdvisorTab() {
        this.$el.find("li").eq(2).hide();
    }

    renderTripAdvisor() {
        if (this.tripAdvisorRendered) {
            return;
        }

        new Vue({
            render: (h) => h(HotelReviews, {
                props: {
                    hotelId: window.appConfig.hotelInfo?.id,
                    initialHotelReview: this.tripAdvisorData, 
                }
            }),
        }).$mount('.js-block-tripadviser');

        this.tripAdvisorRendered = true;
    }

    async loadTopHotelsReviews() {
        try {
            await this.topHotels.load();
            this.topHotelReviewsLoaded = true;
            const noReviewElement = document.querySelector('.top-hotels__no-review');
            if(!noReviewElement) {
                this.hotelTrackingService.trackAvailableTophotels();
                document.querySelector('.reviews__container').addEventListener('click', (event) => {
                    if (event?.target?.closest('.TH-widget-review-wrap__big-link')) {
                        this.hotelTrackingService.trackReviewsTopHotelsMoreClick();
                    }
                })
            }
        } catch (error) {
            console.warn(error);
            this.$el.tabs('disable', 0);
        }
    }
    
    loadTravelataReviews() {
        this.ourReviewsList.load();
    }

    initTabEvents() {
        this.$el.one('click', () => {
            const sourceMap = {
                0: 'topHotels',
                1: 'travelata',
                2: 'tripadviser',
            }
            this.hotelTrackingService.trackReviewsSourceClick(sourceMap[this.$el.tabs('option', 'active')]);
            this.$el.on('tabsactivate', () => {
                this.hotelTrackingService.trackReviewsSourceClick(sourceMap[this.$el.tabs('option', 'active')]);
            });
        });

        this.$el.on('tabsactivate', (event) => {
            if (this.$el.tabs('option', 'active') === 2) {
                this.renderTripAdvisor();
                if(event.originalEvent) this.hotelTrackingService.trackReviewsTripadvisorClick();
            } else if (this.$el.tabs('option', 'active') === 1) {
                this.loadTravelataReviews();
                if(event.originalEvent) this.hotelTrackingService.trackReviewsTravelataClick();
            } else if (!this.topHotelReviewsLoaded) {
                this.loadTopHotelsReviews();
                if(event.originalEvent) this.hotelTrackingService.trackReviewsTopHotelsClick();
            }
        });
    }
}